import {UIController} from "../UIController";

/******************************************************************
 * VideoBlockController
 *
 * @author matthias.schulz@jash.de
 *****************************************************************/

export class VideoBlockController extends UIController {

    /******************************************************************
     * Properties
     *****************************************************************/

    private readonly _videoElement: HTMLVideoElement;
    private readonly _desktopVideoURL: string;
    private readonly _mobileVideoURL: string;
    private readonly _controlsFrame: HTMLElement;
    private readonly _playButton: HTMLElement;
    private readonly _pauseButton: HTMLElement;
    private readonly _unmuteButton: HTMLElement;
    private readonly _muteButton: HTMLElement;
    private _autoplay: boolean;
    private _resizeObserver: ResizeObserver;
    private _mouseInactivityTimeout: number | null = null;

    /******************************************************************
     * Constructor
     *****************************************************************/

    constructor(rootElement: HTMLElement) {
        super(rootElement);
        this._videoElement = rootElement.getElementsByTagName("video")[0] as HTMLVideoElement;
        this._controlsFrame = this.getElementByClass("kkt-videoblock-controls");
        this._playButton = this.getElementByClass("kkt-videoblock-playbutton");
        this._pauseButton = this.getElementByClass("kkt-videoblock-pausebutton");
        this._unmuteButton = this.getElementByClass("kkt-videoblock-unmutebutton");
        this._muteButton = this.getElementByClass("kkt-videoblock-mutebutton");
        this._autoplay = this._videoElement.dataset.autoplay == "true";
        this._desktopVideoURL = this._videoElement.dataset.desktopUrl
        this._mobileVideoURL = this._videoElement.dataset.mobileUrl
        this.initResizeObserver()
        this.updateVideoSource();
        this.initControls();
    }

    /******************************************************************
     * Private Methodes
     *****************************************************************/

    private initResizeObserver() {
        this._resizeObserver = new ResizeObserver(() => {
            this.updateVideoSource();
        });
        this._resizeObserver.observe(this._videoElement);
    }

    private updateVideoSource() {
        const currentSource = this._videoElement.querySelector("source")?.src
        const newSource = this._videoElement.offsetWidth < 768
            ? this._mobileVideoURL
            : this._desktopVideoURL;
        if (currentSource == newSource) return
        this._videoElement.innerHTML = "";
        const source = document.createElement("source");
        source.src = newSource;
        source.type = "video/mp4";
        this._videoElement.appendChild(source);
        if (this._autoplay) {
            this._videoElement.play();
        } else {
            this._videoElement.pause();
        }
    }

    private initControls() {
        this._playButton.addEventListener("click", () => {
            this._videoElement.play();
            this._videoElement.muted = false
        });
        this._pauseButton.addEventListener("click", () => {
            this._videoElement.pause()
            this._autoplay = false;
        });
        this._unmuteButton.addEventListener("click", () => this._videoElement.muted = false);
        this._muteButton.addEventListener("click", () => this._videoElement.muted = true);
        this._videoElement.addEventListener("volumechange", () => {
            if (!this._videoElement.paused) {
                this._unmuteButton.style.display = this._videoElement.muted ? "block" : "none";
            }
            this._muteButton.style.display = this._videoElement.muted ? "none" : "block";
        });
        this._videoElement.addEventListener("mousemove", () => this.resetMouseInactivityTimeout(true));
        this._videoElement.addEventListener("play", () => {
            if (!this._autoplay) {
                this._pauseButton.style.display = "block";
            }
            this._playButton.style.display = "none";
            this.resetMouseInactivityTimeout();
        });
        this._videoElement.addEventListener("pause", () => {
            this._playButton.style.display = "block";
            this._pauseButton.style.display = "none";
            if (this._mouseInactivityTimeout !== null) {
                clearTimeout(this._mouseInactivityTimeout);
                this._mouseInactivityTimeout = null;
            }
            this.showControls();
        });
        if (this._autoplay) {
            this._playButton.style.display = "none";
            this._pauseButton.style.display = "none";
            this._unmuteButton.style.display = "block";
        } else {
            this._unmuteButton.style.display = "none";
            this._playButton.style.display = "block";
            this._pauseButton.style.display = "none";
        }
    }

    private showControls() {
        this._controlsFrame.style.opacity = "1";
        this._controlsFrame.style.pointerEvents = "auto";
    }

    private hideControls() {
        if (this._videoElement.paused) return
        this._controlsFrame.style.opacity = "0";
        this._controlsFrame.style.pointerEvents = "none";
    }

    private resetMouseInactivityTimeout(fromMousemoveEvent: boolean = false) {
        if (fromMousemoveEvent) {
            if (!this._videoElement.paused) {
                this._pauseButton.style.display = "block";
            }
        }
        clearTimeout(this._mouseInactivityTimeout);
        this.showControls();
        this._mouseInactivityTimeout = window.setTimeout(() => {
            this.hideControls();
        }, 2000);
    }

}
